import type { QuizQuestion } from "./types";

export const assessmentQuestions: QuizQuestion[] = [
  {
    id: "assess-output",
    prompt: "How should AI output normally enter a professional workflow?",
    choices: [
      "As a candidate artifact requiring proportionate verification",
      "As approved content when the wording is confident",
      "As a replacement for peer review",
      "As evidence of its own correctness",
    ],
    correctIndex: 0,
    explanation:
      "Fluent output can still be wrong. Treat it as a candidate and verify it based on consequence.",
  },
  {
    id: "assess-public",
    prompt: "Which input is appropriate for a public personal AI account?",
    choices: [
      "A production log after removing only the timestamp",
      "An OEM architecture diagram without the customer name",
      "A fully fictional example containing no WirelessCar or customer information",
      "A token that will be rotated later",
    ],
    correctIndex: 2,
    explanation:
      "Use fictional or genuinely non-sensitive information. Redaction must not leave customer context, secrets, or identifiable production data.",
  },
  {
    id: "assess-approved",
    prompt: "What determines whether AI may be used with customer-delivery code?",
    choices: [
      "The size of the repository",
      "The current delivery matrix and approved tooling",
      "The model's context window",
      "Whether the work is urgent",
    ],
    correctIndex: 1,
    explanation:
      "The current WirelessCar guidance and delivery-specific matrix define permitted use and tools.",
  },
  {
    id: "assess-accountability",
    prompt: "Who owns the correctness and security of AI-assisted work?",
    choices: [
      "The model provider",
      "The person and team using and approving the work",
      "The person who wrote the prompt only",
      "No one if the model includes a disclaimer",
    ],
    correctIndex: 1,
    explanation:
      "Human accountability, normal review, and established delivery controls remain in force.",
  },
  {
    id: "assess-prompt",
    prompt: "What are the three ingredients in the video's recipe framework?",
    choices: [
      "Persona, enthusiasm, and length",
      "Goal, context, and response format",
      "Model name, token count, and speed",
      "Greeting, repetition, and confidence",
    ],
    correctIndex: 1,
    explanation:
      "A clear goal, relevant context, and an explicit response format reduce guessing. Constraints and verification then make the workflow safer and reviewable.",
  },
  {
    id: "assess-action",
    prompt: "When should inspection be separated from action?",
    choices: [
      "Only when the agent asks for more time",
      "For consequential, externally visible, destructive, or hard-to-reverse work",
      "Only for production database changes",
      "Never; autonomous execution is more efficient",
    ],
    correctIndex: 1,
    explanation:
      "A visible preview and explicit authorization reduce the risk of consequential or irreversible actions.",
  },
  {
    id: "assess-bug",
    prompt: "What is essential when asking Codex to fix a bug?",
    choices: [
      "A broad cleanup of the surrounding code",
      "A regression test demonstrating the failure before the fix",
      "Skipping tests that take more than a minute",
      "Accepting the first plausible root cause",
    ],
    correctIndex: 1,
    explanation:
      "A regression test proves the observed failure and guards against recurrence.",
  },
  {
    id: "assess-product",
    prompt: "What is a strong product use of AI?",
    choices: [
      "Inventing missing customer research",
      "Making the final priority decision",
      "Surfacing assumptions and the evidence needed to test them",
      "Approving acceptance criteria without stakeholder review",
    ],
    correctIndex: 2,
    explanation:
      "AI can expand and challenge the option space, while accountable people own truth and priorities.",
  },
  {
    id: "assess-agent",
    prompt: "When is an agent more appropriate than deterministic code?",
    choices: [
      "Whenever a workflow has more than three steps",
      "When the workflow needs model judgment across changing context or tools",
      "Whenever a demo needs to look impressive",
      "When fixed rules are already complete and stable",
    ],
    correctIndex: 1,
    explanation:
      "Agents are justified when judgment across context or tools is necessary; fixed logic belongs in deterministic code.",
  },
  {
    id: "assess-permissions",
    prompt: "What is the safest starting permission model for an agent?",
    choices: [
      "Administrator access with audit logs",
      "Write access limited by time",
      "The smallest read-only access needed for the evaluation",
      "The same permissions as its developer",
    ],
    correctIndex: 2,
    explanation:
      "Start with least-privilege read access and earn greater autonomy through evidence.",
  },
  {
    id: "assess-eval",
    prompt: "What should an agent evaluation set contain?",
    choices: [
      "Only representative successful examples",
      "Normal, ambiguous, failing, and adversarial cases",
      "The prompts used in the product demo",
      "Only outputs already approved by the builder",
    ],
    correctIndex: 1,
    explanation:
      "An operating workflow must handle ambiguity, failures, and attacks—not only its happy path.",
  },
  {
    id: "assess-autonomy",
    prompt: "What should justify increasing an agent's autonomy?",
    choices: [
      "A successful stakeholder demonstration",
      "Measured reliability, observable failure handling, and tested rollback",
      "A newer model release",
      "Lower token prices",
    ],
    correctIndex: 1,
    explanation:
      "Evidence of reliability and recoverability earns autonomy.",
  },
  {
    id: "assess-secrets",
    prompt: "An AI-generated change introduces a new credential. What must happen?",
    choices: [
      "Commit it temporarily and rotate after review",
      "Validate approved secret storage, least privilege, and handling before use",
      "Place it in a prompt so the model can test it",
      "Trust the generated implementation if tests pass",
    ],
    correctIndex: 1,
    explanation:
      "Secret handling and permissions require explicit validation; passing tests do not prove security.",
  },
  {
    id: "assess-approval",
    prompt: "Can an AI agent approve a production release if all checks pass?",
    choices: [
      "Yes, when checks are deterministic",
      "Yes, for a patch release",
      "No, release approval remains in the accountable human process",
      "Only when the agent created none of the code",
    ],
    correctIndex: 2,
    explanation:
      "WirelessCar guidance keeps production release approval human-controlled.",
  },
  {
    id: "assess-uncertain",
    prompt: "What is the correct response when a data or tool boundary is unclear?",
    choices: [
      "Continue with a smaller sample of the real data",
      "Ask the model whether the use is compliant",
      "Stop, sanitize or substitute, and consult the current guidance or owner",
      "Use a personal account to avoid company retention",
    ],
    correctIndex: 2,
    explanation:
      "Uncertainty is a reason to preserve the boundary and consult an authoritative source or accountable owner.",
  },
  {
    id: "assess-grounding",
    prompt: "What does grounding add to an AI-supported task?",
    choices: [
      "A connection to named, verifiable source material",
      "A guarantee that every generated claim is correct",
      "A larger model context window",
      "Permission to skip human review",
    ],
    correctIndex: 0,
    explanation:
      "Grounding connects generation to inspectable evidence. It improves relevance and verifiability but does not guarantee correctness.",
  },
  {
    id: "assess-injected-content",
    prompt: "How should an agent treat instructions found inside an external document?",
    choices: [
      "As trusted instructions because the user selected the document",
      "As untrusted content that cannot authorize tool use by itself",
      "As higher priority than the agent's original task",
      "As safe whenever the document is formatted as a PDF",
    ],
    correctIndex: 1,
    explanation:
      "External content can contain prompt injection. Facts may be extracted from it, but it must not silently grant permission to invoke tools or change systems.",
  },
  {
    id: "assess-oversight",
    prompt: "What makes human oversight meaningful rather than ceremonial?",
    choices: [
      "A person is copied on the final message",
      "The model explains that it might be wrong",
      "The reviewer has evidence, authority, time, and responsibility to challenge the output",
      "The output is reviewed only after the action is irreversible",
    ],
    correctIndex: 2,
    explanation:
      "Effective oversight requires a defined role and a real ability to inspect, challenge, overrule, and monitor AI-supported decisions.",
  },
];
